import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import Service from "@/services/KepatuhanJatuhService";
import {
  KepatuhanJatuhSession,
  KepatuhanJatuhObservation,
  KepatuhanJatuhKasusRtl,
  KepatuhanJatuhAlasan,
  KepatuhanJatuhRca,
  SignTier,
  DashboardData
} from "@/types/KepatuhanJatuh";

@Module({
  store,
  dynamic: true,
  namespaced: true,
  name: "kepatuhan-jatuh"
})
class KepatuhanJatuhModule extends VuexModule {
  // --- State ---
  sessions: KepatuhanJatuhSession[] = [];
  currentSession: KepatuhanJatuhSession | null = null;
  dashboardData: DashboardData | null = null;
  instruments: any[] = [];
  loading = false;
  pagination = {
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0
  };

  // --- Mutations ---

  @Mutation SET_SESSIONS(val: KepatuhanJatuhSession[]) {
    this.sessions = val;
  }

  @Mutation SET_CURRENT(val: KepatuhanJatuhSession | null) {
    this.currentSession = val;
  }

  @Mutation SET_DASHBOARD(val: DashboardData | null) {
    this.dashboardData = val;
  }

  @Mutation SET_INSTRUMENTS(val: any[]) {
    this.instruments = val;
  }

  @Mutation SET_LOADING(val: boolean) {
    this.loading = val;
  }

  @Mutation SET_PAGINATION(val: any) {
    this.pagination = val;
  }

  // --- Actions ---

  @Action
  async fetchList(params: any = {}) {
    this.SET_LOADING(true);
    try {
      const dt = await Service.list(params);
      const resp = dt.data;
      this.SET_SESSIONS(resp.data?.data || resp.data || []);
      if (resp.meta) {
        this.SET_PAGINATION({
          currentPage: resp.meta.current_page,
          lastPage: resp.meta.last_page,
          perPage: resp.meta.per_page,
          total: resp.meta.total
        });
      }
      return resp;
    } catch (e) {
      console.error("KepatuhanJatuh fetchList error:", e);
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action
  async fetchOne(id: number) {
    this.SET_LOADING(true);
    try {
      const dt = await Service.show(id);
      const data = dt.data.data || dt.data || null;
      this.SET_CURRENT(data);
      return data;
    } catch (e) {
      console.error("KepatuhanJatuh fetchOne error:", e);
    } finally {
      this.SET_LOADING(false);
    }
  }

  @Action
  async create(data: Partial<KepatuhanJatuhSession>) {
    const dt = await Service.create(data);
    const created = dt.data.data || dt.data;
    this.SET_CURRENT(created);
    return created;
  }

  @Action
  async update(payload: { id: number; data: Partial<KepatuhanJatuhSession> }) {
    const dt = await Service.update(payload.id, payload.data);
    const updated = dt.data.data || dt.data;
    this.SET_CURRENT(updated);
    return updated;
  }

  @Action
  async destroy(id: number) {
    await Service.destroy(id);
  }

  @Action
  async fetchInstruments(params: any = {}) {
    try {
      const dt = await Service.listInstruments(params);
      const list = dt.data.data || dt.data || [];
      this.SET_INSTRUMENTS(list);
      return list;
    } catch (e) {
      console.error("KepatuhanJatuh fetchInstruments error:", e);
    }
  }

  @Action
  async addObservation(payload: {
    id: number;
    data: Partial<KepatuhanJatuhObservation>;
  }) {
    const dt = await Service.addObservation(payload.id, payload.data);
    await this.fetchOne(payload.id);
    return dt.data.data || dt.data;
  }

  @Action
  async updateObservation(payload: {
    id: number;
    obsId: number;
    data: Partial<KepatuhanJatuhObservation>;
  }) {
    const dt = await Service.updateObservation(
      payload.id,
      payload.obsId,
      payload.data
    );
    await this.fetchOne(payload.id);
    return dt.data.data || dt.data;
  }

  @Action
  async deleteObservation(payload: { id: number; obsId: number }) {
    await Service.deleteObservation(payload.id, payload.obsId);
    await this.fetchOne(payload.id);
  }

  @Action
  async classifyRisk(payload: {
    id: number;
    skor: number;
    instrumentId: number;
  }) {
    const dt = await Service.classifyRisk(
      payload.id,
      payload.skor,
      payload.instrumentId
    );
    return dt.data.data || dt.data;
  }

  @Action
  async autoPopulateRtl(id: number) {
    await Service.autoPopulateRtl(id);
    await this.fetchOne(id);
  }

  @Action
  async updateRtl(payload: {
    id: number;
    rtlId: number;
    data: Partial<KepatuhanJatuhKasusRtl>;
  }) {
    await Service.updateRtl(payload.id, payload.rtlId, payload.data);
    await this.fetchOne(payload.id);
  }

  @Action
  async saveAlasan(payload: { id: number; data: KepatuhanJatuhAlasan }) {
    await Service.saveAlasan(payload.id, payload.data);
    await this.fetchOne(payload.id);
  }

  @Action
  async saveRca(payload: { id: number; items: KepatuhanJatuhRca[] }) {
    await Service.saveRca(payload.id, payload.items);
    await this.fetchOne(payload.id);
  }

  @Action
  async sign(payload: {
    id: number;
    tier: SignTier;
    password: string;
    tanggalVerifikasi?: string;
  }) {
    await Service.sign(
      payload.id,
      payload.tier,
      payload.password,
      payload.tanggalVerifikasi
    );
    await this.fetchOne(payload.id);
  }

  @Action
  async publish(id: number) {
    await Service.publish(id);
    await this.fetchOne(id);
  }

  @Action
  async revoke(id: number) {
    await Service.revoke(id);
    await this.fetchOne(id);
  }

  @Action
  async fetchDashboard(tahun: number) {
    try {
      const dt = await Service.dashboard(tahun);
      const data = dt.data.data || dt.data || null;
      this.SET_DASHBOARD(data);
      return data;
    } catch (e) {
      console.error("KepatuhanJatuh fetchDashboard error:", e);
    }
  }

  @Action
  async fetchRekap(params: any = {}) {
    const dt = await Service.rekap(params);
    return dt.data.data || dt.data;
  }

  @Action
  async generateRekap(params: any) {
    const dt = await Service.generateRekap(params);
    return dt.data.data || dt.data;
  }
}

export default getModule(KepatuhanJatuhModule);
